/**
 * Tournament Notification Service
 * 
 * Handles push notifications for tournament events:
 * - Registration confirmation
 * - Match start reminders
 * - Live match alerts
 * - Victory/defeat results
 */

import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { supabase } from '../lib/supabase';

// Configure notification behavior
Notifications.setNotificationHandler({
    handleNotification: async () => ({
        shouldShowAlert: true,
        shouldPlaySound: true,
        shouldSetBadge: true,
    }),
});

/**
 * Request notification permissions and get push token
 */
export const registerForPushNotifications = async () => {
    let token = null;

    // Check if physical device (notifications don't work on simulators)
    if (!Device.isDevice) {

        return null;
    }

    // Check existing permissions
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;

    // Request permissions if not granted
    if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
    }

    if (finalStatus !== 'granted') {

        return null;
    }

    // Get Expo push token
    try {
        const tokenData = await Notifications.getExpoPushTokenAsync({
            projectId: 'ce9736d1-f815-4a5b-a1f2-19c7ad39a140',
        });
        token = tokenData.data;

    } catch (error) {

    }

    // Android-specific channel setup
    if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('tournament', {
            name: 'Torneio',
            importance: Notifications.AndroidImportance.MAX,
            vibrationPattern: [0, 250, 250, 250],
            lightColor: '#A855F7',
            sound: 'default',
        });

        await Notifications.setNotificationChannelAsync('match', {
            name: 'Partidas',
            importance: Notifications.AndroidImportance.HIGH,
            vibrationPattern: [0, 500],
            lightColor: '#EF4444',
            sound: 'default',
        });
    }

    return token;
};

/**
 * Save push token to user profile in database
 */
export const savePushToken = async (userId, token) => {
    if (!userId || !token) return;

    try {
        await supabase
            .from('profiles')
            .update({ push_token: token })
            .eq('id', userId);


    } catch (error) {

    }
};

/**
 * Schedule a local notification
 */
export const scheduleLocalNotification = async ({
    title,
    body,
    data = {},
    trigger = null, // null = immediate, or { seconds: 5 } for delay
    channelId = 'tournament',
}) => {
    try {
        const notificationId = await Notifications.scheduleNotificationAsync({
            content: {
                title,
                body,
                data,
                sound: 'default',
                ...(Platform.OS === 'android' && { channelId }),
            },
            trigger,
        });


        return notificationId;
    } catch (error) {

        return null;
    }
};

/**
 * Tournament-specific notification helpers
 */
export const TournamentNotifications = {
    // Registration confirmed
    registrationConfirmed: async (tournamentNumber) => {
        return scheduleLocalNotification({
            title: '✅ Inscrição Confirmada!',
            body: `Você está inscrito no Torneio #${tournamentNumber}. Boa sorte!`,
            data: { type: 'registration', tournamentNumber },
        });
    },

    // Match starting soon (5 min warning)
    matchStartingSoon: async (opponentName, tournamentNumber) => {
        return scheduleLocalNotification({
            title: '⚔️ Partida em 5 minutos!',
            body: `Prepare-se! Sua partida contra @${opponentName} começa em breve.`,
            data: { type: 'match_soon', opponentName },
            channelId: 'match',
        });
    },

    // Match is live NOW
    matchLiveNow: async (opponentName, matchId) => {
        return scheduleLocalNotification({
            title: '🔴 PARTIDA AO VIVO!',
            body: `Sua partida contra @${opponentName} começou! Clique para jogar.`,
            data: { type: 'match_live', matchId, opponentName },
            channelId: 'match',
        });
    },

    // Victory notification
    matchVictory: async (opponentName, score, nextRound) => {
        return scheduleLocalNotification({
            title: '🎉 VITÓRIA!',
            body: `Você venceu @${opponentName} por ${score}! ${nextRound ? `Próxima rodada: ${nextRound}` : 'Parabéns, campeão!'}`,
            data: { type: 'victory', opponentName },
        });
    },

    // Defeat notification
    matchDefeat: async (opponentName, score) => {
        return scheduleLocalNotification({
            title: '😔 Fim da jornada',
            body: `Você perdeu para @${opponentName} por ${score}. Volte semana que vem!`,
            data: { type: 'defeat', opponentName },
        });
    },

    // Champion notification
    championCelebration: async (tournamentNumber) => {
        return scheduleLocalNotification({
            title: '🏆 VOCÊ É O CAMPEÃO!',
            body: `Parabéns! Você venceu o Torneio #${tournamentNumber}! +500 XP conquistados!`,
            data: { type: 'champion', tournamentNumber },
        });
    },

    // Round starting
    roundStarting: async (roundName, tournamentNumber) => {
        return scheduleLocalNotification({
            title: `📣 ${roundName} Iniciando!`,
            body: `A rodada está começando no Torneio #${tournamentNumber}. Confira seu oponente!`,
            data: { type: 'round_start', roundName },
        });
    },
};

/**
 * Cancel all scheduled notifications
 */
export const cancelAllNotifications = async () => {
    await Notifications.cancelAllScheduledNotificationsAsync();

};

/**
 * Set up notification response listener
 */
export const setupNotificationResponseListener = (navigation) => {
    const subscription = Notifications.addNotificationResponseReceivedListener(response => {
        const data = response.notification.request.content.data;

        // Handle navigation based on notification type
        switch (data.type) {
            case 'match_live':
                navigation.navigate('MatchScreen', { matchId: data.matchId });
                break;
            case 'match_soon':
            case 'round_start':
                navigation.navigate('TournamentHub');
                break;
            case 'victory':
            case 'defeat':
            case 'champion':
                navigation.navigate('MyTournamentStats');
                break;
            default:
                navigation.navigate('TournamentHub');
        }
    });

    return subscription;
};

export default {
    registerForPushNotifications,
    savePushToken,
    scheduleLocalNotification,
    cancelAllNotifications,
    setupNotificationResponseListener,
    TournamentNotifications,
};
